import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Leaf } from "lucide-react";
import { useSettings } from "@/context/AppContext";
import { sizeNavbarLink, sizeNavbarBtn } from "@/lib/textSize";

const links = [
  { to: "/", label: "Accueil" },
  { to: "/a-propos", label: "À propos" },
  { to: "/services", label: "Services" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { settings } = useSettings();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 24);
    h();
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [loc.pathname]);

  const linkStyle = { fontSize: sizeNavbarLink(settings?.navbar_link_size) };
  const btnStyle = { fontSize: sizeNavbarBtn(settings?.navbar_btn_size) };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl backdrop-saturate-150 border-b border-forest/10"
          : "bg-transparent"
      }`}
    >
      <div className="container-px max-w-7xl mx-auto h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo" className="flex items-center gap-2.5 group">
          {settings?.logo ? (
            <img
              src={settings.logo}
              alt={settings?.company_name || "Logo"}
              style={{ height: `${Math.max(24, Math.min(120, settings?.logo_size || 40))}px` }}
              className="w-auto max-w-[260px] object-contain"
            />
          ) : (
            <>
              <span className="w-9 h-9 rounded-full bg-forest text-cream flex items-center justify-center">
                <Leaf className="w-4 h-4" strokeWidth={1.5} />
              </span>
              <span className="font-display text-2xl tracking-tight text-forest leading-none">
                {settings?.company_name || "Pizzelli"}
              </span>
            </>
          )}
        </Link>

        <nav className="hidden lg:flex items-center gap-9">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.to === "/"}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              style={linkStyle}
              className={({ isActive }) =>
                `tracking-wide link-underline ${
                  isActive ? "text-forest" : "text-forest-soft hover:text-forest"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link to="/contact" data-testid="navbar-cta-quote" className="btn-primary" style={btnStyle}>
            {settings?.cta_label || "Demander un devis"}
          </Link>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden w-10 h-10 flex items-center justify-center text-forest"
          data-testid="navbar-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div
          className="lg:hidden bg-cream border-t border-forest/10 animate-fade-up"
          data-testid="navbar-mobile-menu"
        >
          <div className="container-px py-6 flex flex-col gap-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                className={({ isActive }) =>
                  `py-3 text-base ${isActive ? "text-forest" : "text-forest-soft"}`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary mt-4 w-full" data-testid="mobile-cta-quote">
              {settings?.cta_label || "Demander un devis"}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
